import { useEmailParticipantsAPI } from '@/src/hooks/api/useEmailParticipantsAPI';
import useDebounceCallback from '@/src/hooks/useDebounceCallback';
import { useEvents } from '@/src/store/events.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { readCsv } from '@/src/utils/csv.utils';
import { eventParticipantValidator } from '@/src/utils/event.validation.utils';
import { errorFlashbar } from '@/src/utils/notification.utils';
import { isValidEmail } from '@/src/utils/validation.utils';
import {
  Box,
  Button,
  ButtonDropdown,
  Container,
  FileUpload,
  FormField,
  Grid,
  Header,
  Icon,
  Input,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  TokenGroup,
} from '@amzn/awsui-components-react';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventParticipantsFields } from '../../../types/Event';
import { i18nKeys } from '../../../utils/i18n.utils';
import { ConfirmModal } from '../ConfirmModal';
import { EventAudienceType } from '@/src/constants/shared';

interface ParticipantsProps {
  isClassroomsEvent: boolean;
  eventId: string;
}

interface ItokenUser {
  label: string;
}

export const ParticipantInvites: React.FC<ParticipantsProps> = ({ isClassroomsEvent, eventId }) => {
  const { t } = useTranslation();
  const { inviteParticipantModal, event, toggleInviteParticipantModal } = useEvents();
  const { addFlashbar, clearFlashbars } = useFlashbars();
  const emailParticipantsAPI = useEmailParticipantsAPI();
  const [participantEmail, setParticipantEmail] = useState('');
  const [csvFile, setCSVFile] = useState<File[] | undefined>([]);
  const [participantEmailError, setParticipantEmailError] = useState('');
  const [participantCsvFileError, setParticipantCsvFileError] = useState('');
  const [confirmMultiResendInvitationVisible, setConfirmMultiResendInvitationVisible] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [removeParticipantModal, setRemoveParticipantModal] = useState(false);
  const [addParticipantModal, setAddParticipantModal] = useState(false);
  const [csvInviteModal, setCsvInviteModal] = useState(false);

  const [csvFormData, setCsvFormData] = useState<FormData | undefined>();
  const [csvUploadedEmailList, setCsvUploadedEmailList] = useState<string[]>([]);
  const [tempUserEmail, setTempUserEmail] = useState<ItokenUser[]>([]);
  const [loading, setLoading] = useState(false);

  const [participantList, setParticipantList] = useState<{ name: string }[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<{ name: string }[]>([]);
  const [filterText, setFilterText] = useState('');
  const [pageToken, setPageToken] = useState<string>();
  const [currPageIndex, setCurrPageIndex] = useState(1);
  const [totalParticipantCount, setTotalParticipantCount] = useState(0);

  const pageSize = 20;
  const pageCount = Math.ceil((participantList || []).length / pageSize) + (!!pageToken ? 1 : 0);

  const handleMultiReInviteParticipants = async () => {
    if (selectedItems?.length > 0) {
      setConfirmMultiResendInvitationVisible(false);
      const participantEmailAsString = selectedItems?.map((item) => (item?.name ? item?.name : '')) || [];
      await emailParticipantsAPI.inviteParticipant(eventId, {
        emails: participantEmailAsString,
      });
    }
  };

  // on pagination click userList updates
  const onPageClick = async (newPageIndex: number) => {
    setCurrPageIndex(newPageIndex);

    if (newPageIndex !== pageCount || !pageToken) {
      return;
    }

    setLoadingTable(true);

    const res = await emailParticipantsAPI.participantList(eventId, pageSize, pageToken);

    const modifiedEmailList = res?.participantsEmail?.map((item) => ({ name: item }));
    setParticipantList([...participantList, ...modifiedEmailList]);
    setPageToken(res?.nextToken);

    setLoadingTable(false);
  };

  const getParticipantList = async () => {
    setLoadingTable(true);

    void getParticipantCount();
    const res = await emailParticipantsAPI.participantList(eventId, pageSize);

    const modifiedEmailList = res?.participantsEmail?.map((item) => ({ name: item }));
    setParticipantList(modifiedEmailList);
    setPageToken(res?.nextToken);
    setSelectedItems([]);

    setLoadingTable(false);
  };

  const getParticipantCount = async () => {
    const countResponse = await emailParticipantsAPI.countParticipant(eventId);

    setTotalParticipantCount(Number(countResponse?.count) || 0);
  };

  useEffect(() => {
    void getParticipantList();
  }, []);

  const validator = useMemo(
    () =>
      eventParticipantValidator(
        participantEmail,
        t(i18nKeys.settings.errors.invalidEmailFormat),
        new Map<EventParticipantsFields, (error: string) => void>([
          [EventParticipantsFields.PARTICIPANT, (error: string) => setParticipantEmailError(error)],
        ])
      ),
    [participantEmail, eventParticipantValidator]
  );

  const downloadTemplate = async () => {
    const res: Blob = await emailParticipantsAPI.getTemplateParticipant(eventId);
    const downloadFile = () => {
      const url = window.URL.createObjectURL(res);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'InviteParticipantTemplate.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    };
    downloadFile();
  };

  const onParticipantSearchUpdate = async (value: string) => {
    setFilterText(value);

    if (!value) {
      void getParticipantList();
    } else {
      void debounceSearchEmail(value);
    }
  };

  const searchEmail = async (value: string) => {
    setLoadingTable(true);

    const res: string[] = (await emailParticipantsAPI.searchParticipant(eventId, value)) || [];
    const modifiedEmailList = res?.map((item) => ({ name: item }));

    setParticipantList(modifiedEmailList);
    setPageToken(undefined);
    setSelectedItems([]);
    setTotalParticipantCount(modifiedEmailList.length);

    setLoadingTable(false);
  };

  const debounceSearchEmail = useDebounceCallback((value: string) => searchEmail(value), 500);

  const onPapaParseComplete = (results: Papa.ParseResult<any>, file: File) => {
    const formData = new FormData();
    formData.append('name', 'InviteParticipantTemplate');
    formData.append('file', file);

    const participantEmails: string[] = [];
    const resultsList = _.flatMap(results.data);

    // Skip row 0 which is the header
    for (let i = 1; i < resultsList.length; i++) {
      const singleEmail = resultsList[i] as string;

      if (!isValidEmail(singleEmail)) {
        setParticipantCsvFileError(t(i18nKeys.participants.addParticipants.importCSV.uploadFailed));
        return;
      }

      participantEmails.push(singleEmail);
    }

    setCsvUploadedEmailList(participantEmails);
    setCsvFormData(formData);
  };

  /**
   *
   * @param files
   * handle csv upload and add participants
   */
  const handleCSVUpload = async (files: File[] | undefined) => {
    if (handleMaxParticipantsValidation()) {
      return;
    }
    setParticipantCsvFileError('');

    if (files && files[0]) {
      const parsed = await readCsv(files[0]);

      onPapaParseComplete(parsed.results, parsed.file);
    }

    setCSVFile(files);
  };

  const sendCsvToServer = async () => {
    setLoading(true);
    setCsvInviteModal(false);
    try {
      await emailParticipantsAPI.inviteUploadParticipant(eventId, csvFormData);
      setCSVFile([]);
      void getParticipantList();
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      throw e;
    }
  };

  const handleRemoveParticipants = async () => {
    setRemoveParticipantModal(false);
    const newData = participantList?.filter(
      ({ name: email1 }) => !selectedItems?.some(({ name: email2 }) => email1 === email2)
    );
    setParticipantList(newData);
    const participantEmailAsString = selectedItems?.map((item) => (item?.name ? item?.name : '')) || [];
    await emailParticipantsAPI.removeParticipants(eventId, {
      emails: participantEmailAsString,
    });
    void getParticipantList();
  };

  const sendEmailToParticipant = async () => {
    try {
      setAddParticipantModal(false);
      setLoading(true);
      const data = [...tempUserEmail];
      const useEmailAsListOfArr = data?.map((item) => item.label);
      await emailParticipantsAPI.createParticipant(eventId, {
        emails: useEmailAsListOfArr,
      });
      setTempUserEmail([]);
      void getParticipantList();
    } catch (e) {
      // error showed by API
    }
    setLoading(false);
  };

  const handleAddParticipants = () => {
    if (validator.isValidSection(true)) {
      if (handleMaxParticipantsValidation()) {
        setParticipantEmail('');
        return;
      }
      setTempUserEmail([{ label: participantEmail }, ...tempUserEmail]);
      setParticipantEmail('');
    }
  };

  const handleMaxParticipantsValidation = () => {
    if (
      event?.maxExpectedParticipants &&
      participantList.length + tempUserEmail.length >= event?.maxExpectedParticipants
    ) {
      clearFlashbars();
      addFlashbar(
        errorFlashbar(
          t(i18nKeys.participants.addParticipants.error, {
            count: participantList.length + tempUserEmail.length,
            max: event?.maxExpectedParticipants,
          }),
          ''
        )
      );
      return true;
    }
  };

  /**
   * handle dropdown action click
   */

  const handleActionClick = (clickEvent: any) => {
    if (clickEvent.detail.id === 'resendInvite') {
      setConfirmMultiResendInvitationVisible(true);
    }
  };

  return (
    <React.Fragment>
      <SpaceBetween direction="vertical" size="s">
        <Container header={<Header variant="h1">{t(i18nKeys.participants.invitesTitle)}</Header>}>
          <Grid gridDefinition={[{ colspan: 5 }, { colspan: 1 }, { colspan: 6 }]}>
            {!isClassroomsEvent && (
              <>
                <div>
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddParticipants();
                    }}
                  >
                    <SpaceBetween size="m" direction="vertical">
                      <Box>
                        <FormField
                          errorText={participantEmailError}
                          description={
                            event?.audienceType === EventAudienceType.SKILL_BUILDER_EVENT ||
                            event?.audienceType === EventAudienceType.SKILL_BUILDER_LEARNER_EVENT
                              ? t(i18nKeys.participants.addParticipants.caption)
                              : ''
                          }
                          label={t(i18nKeys.participants.addParticipants.title)}
                          data-testid="add-participant-form"
                        >
                          <Input
                            value={participantEmail}
                            type="email"
                            placeholder="alias@domain.com"
                            onChange={({ detail }) => setParticipantEmail(detail.value)}
                            onBlur={() =>
                              participantEmail && validator.isValidField(EventParticipantsFields.PARTICIPANT)
                            }
                            data-testid="add-participant-input"
                          />
                        </FormField>
                        <Box margin={{ top: 's' }}>
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="submit" data-testid="add-participant-button">
                              {t(i18nKeys.general.add)}
                            </Button>
                            <Button
                              formAction="none"
                              loading={loading}
                              variant="primary"
                              disabled={!(tempUserEmail.length > 0)}
                              onClick={() => tempUserEmail?.length > 0 && setAddParticipantModal(true)}
                              data-testid="send-participant-invite-button"
                            >
                              {`${t(i18nKeys.participants.buttons.saveEmailInviteOne)} ${
                                tempUserEmail?.length > 0 ? tempUserEmail?.length : ''
                              } ${t(i18nKeys.participants.buttons.saveEmailInviteTwo)}`}
                            </Button>
                          </SpaceBetween>
                        </Box>
                        <Box>
                          <TokenGroup
                            onDismiss={({ detail: { itemIndex } }) => {
                              setTempUserEmail([
                                ...tempUserEmail.slice(0, itemIndex),
                                ...tempUserEmail.slice(itemIndex + 1),
                              ]);
                            }}
                            items={tempUserEmail}
                            limit={4}
                            data-testid="pre-invite-participant-tokengroup"
                          />
                        </Box>
                      </Box>
                    </SpaceBetween>
                  </form>
                </div>
                <div style={{ justifyContent: 'center', display: 'flex', marginTop: '25px' }}>
                  <Box>{t(i18nKeys.participants.addParticipants.or)}</Box>
                </div>
                <div>
                  <FormField
                    label={t(i18nKeys.participants.addParticipants.importCSV.title)}
                    description={t(i18nKeys.participants.addParticipants.importCSV.description)}
                    errorText={participantCsvFileError}
                    constraintText={participantCsvFileError && t(i18nKeys.participants.addParticipants.importCSV.hint)}
                    data-testid="bulk-upload-participant-form"
                  >
                    <SpaceBetween direction="horizontal" size="l">
                      <FileUpload
                        onChange={({ detail }) => void handleCSVUpload(detail.value)}
                        accept="text/csv"
                        value={csvFile || []}
                        showFileLastModified
                        showFileSize
                        tokenLimit={3}
                        i18nStrings={{
                          uploadButtonText: (e) =>
                            e
                              ? t(i18nKeys.participants.addParticipants.importCSV.chooseFiles)
                              : t(i18nKeys.participants.addParticipants.importCSV.chooseFile),
                          dropzoneText: (e) =>
                            e
                              ? t(i18nKeys.participants.addParticipants.importCSV.dropFilesToUpload)
                              : t(i18nKeys.participants.addParticipants.importCSV.dropFileToUpload),
                          removeFileAriaLabel: (e) =>
                            `${t(i18nKeys.participants.addParticipants.importCSV.removeFile)}  ${e + 1}`,
                          limitShowFewer: t(i18nKeys.participants.addParticipants.importCSV.showFewerFiles),
                          limitShowMore: t(i18nKeys.participants.addParticipants.importCSV.showMoreFiles),
                          errorIconAriaLabel: t(i18nKeys.participants.addParticipants.importCSV.error),
                        }}
                      />
                      <Box>
                        <Button variant="link" onClick={() => void downloadTemplate()}>
                          <Box variant="span" padding={{ right: 'xs' }} color="inherit">
                            <Icon name="download" />
                          </Box>
                          {t(i18nKeys.participants.addParticipants.downloadTemplate)}
                        </Button>
                      </Box>
                    </SpaceBetween>
                    {csvFile && csvFile?.length > 0 && !participantCsvFileError && (
                      <div style={{ marginTop: '10px' }}>
                        <Button
                          loading={loading}
                          variant="primary"
                          ariaLabel={t(i18nKeys.participants.buttons.inviteCsvBtn)}
                          onClick={() => setCsvInviteModal(true)}
                        >
                          {t(i18nKeys.participants.buttons.inviteCsvBtn)}
                        </Button>
                      </div>
                    )}
                  </FormField>
                </div>
              </>
            )}
            {isClassroomsEvent && <>{t(i18nKeys.participants.classroomsInvitesDisabled)}</>}
          </Grid>
        </Container>
        {/* table container */}
        <Container>
          <Header
            variant="h1"
            counter={`(${
              selectedItems && selectedItems.length > 0 ? `${selectedItems.length}/` : ''
            }${totalParticipantCount})`}
            // remove and action buttons
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={!selectedItems || selectedItems?.length === 0 || isClassroomsEvent}
                  onClick={() => setRemoveParticipantModal(true)}
                  data-testid="remove-participant-button"
                >
                  {t(i18nKeys.general.remove)}
                </Button>
                <ButtonDropdown
                  disabled={!selectedItems || selectedItems?.length === 0}
                  className="mr-5"
                  items={[{ text: 'Resend Invite', id: 'resendInvite', disabled: false }]}
                  onItemClick={handleActionClick}
                  data-testid="invite-actions-dropdown"
                >
                  {t(i18nKeys.events.eventDetails.buttons.actionsPlaceholder)}
                </ButtonDropdown>
              </SpaceBetween>
            }
          >
            {t(i18nKeys.participants.headers.participants)}
          </Header>
          <Table
            loading={loadingTable}
            variant="borderless"
            onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
            selectedItems={selectedItems}
            data-testid="invited-participants-table"
            filter={
              <SpaceBetween direction="horizontal" size="s">
                <TextFilter
                  className="text-filter"
                  filteringPlaceholder={t(i18nKeys.participants.labels.searchParticipants)}
                  filteringAriaLabel={t(i18nKeys.participants.labels.userFilteringLabel)}
                  filteringText={filterText}
                  onChange={({ detail }) => void onParticipantSearchUpdate(detail.filteringText)}
                  data-testid="invited-participants-filter"
                />
              </SpaceBetween>
            }
            columnDefinitions={[
              {
                id: 'variable',
                header: 'Participant',
                cell: (item) => <Link href="#">{item.name}</Link>,
                isRowHeader: true,
              },
            ]}
            columnDisplay={[
              { id: 'variable', visible: true },
              { id: 'value', visible: true },
            ]}
            items={(participantList || []).slice(
              (currPageIndex - 1) * pageSize,
              (currPageIndex - 1) * pageSize + pageSize
            )}
            loadingText="Loading resources"
            selectionType="multi"
            trackBy="name"
            empty={
              participantList?.length === 0 && (
                <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
                  <SpaceBetween size="m">
                    <b>{t(i18nKeys.participants.table.emptyView.title)}</b>
                  </SpaceBetween>
                </Box>
              )
            }
            pagination={
              <Pagination
                currentPageIndex={currPageIndex}
                onChange={({ detail }) => {
                  void onPageClick(detail.currentPageIndex);
                }}
                openEnd={!!pageToken}
                pagesCount={pageCount}
              />
            }
          />
        </Container>
      </SpaceBetween>
      {selectedItems && (
        <>
          <ConfirmModal
            visible={confirmMultiResendInvitationVisible}
            message={`${t(i18nKeys.participants.messages.reInviteparticipant)} ${selectedItems?.length} ${t(
              i18nKeys.participants.messages.participants
            )}`}
            confirmBtnLabel={t(i18nKeys.participants.buttons.confirmReInvitation)}
            onConfirm={() => handleMultiReInviteParticipants()}
            onCancel={() => setConfirmMultiResendInvitationVisible(false)}
            data-testid="resend-invitations-modal"
          />
          <ConfirmModal
            visible={csvInviteModal}
            title={t(i18nKeys.participants.messages.sendInviteCallback)}
            message={`${t(i18nKeys.participants.messages.sendEmailInviteOne)} ${csvUploadedEmailList?.length} ${t(
              i18nKeys.participants.messages.sendEmailInviteTwo
            )} ${t(i18nKeys.participants.messages.sendEmailInviteThree)}`}
            cancelBtnLabel={t(i18nKeys.participants.buttons.goBackBtn)}
            confirmBtnLabel={`${t(i18nKeys.participants.buttons.saveEmailInviteOne)} ${
              csvUploadedEmailList?.length
            } ${t(i18nKeys.participants.buttons.saveEmailInviteTwo)}`}
            onConfirm={() => void sendCsvToServer()}
            onCancel={() => setCsvInviteModal(false)}
            data-testid="invite-from-csv-modal"
          />
          <ConfirmModal
            visible={removeParticipantModal}
            title="Save"
            message={`${t(i18nKeys.participants.messages.removeMsgOne)} ${selectedItems?.length} ${t(
              i18nKeys.participants.messages.removeMsgTwo
            )}`}
            cancelBtnLabel={t(i18nKeys.participants.buttons.goBackBtn)}
            confirmBtnLabel={t(i18nKeys.participants.buttons.saveRemoveBtn)}
            onConfirm={() => void handleRemoveParticipants()}
            onCancel={() => setRemoveParticipantModal(false)}
            data-testid="remove-participant-modal"
          />
          <ConfirmModal
            visible={selectedItems?.length > 0 && inviteParticipantModal}
            title={t(i18nKeys.participants.messages.sendInviteCallback)}
            message={`${t(i18nKeys.participants.messages.sendEmailInviteOne)} ${selectedItems?.length} ${t(
              i18nKeys.participants.messages.sendEmailInviteTwo
            )} ${t(i18nKeys.participants.messages.sendEmailInviteThree)}`}
            cancelBtnLabel={t(i18nKeys.participants.buttons.goBackBtn)}
            confirmBtnLabel={`${t(i18nKeys.participants.buttons.saveEmailInviteOne)} ${selectedItems?.length} ${t(
              i18nKeys.participants.buttons.saveEmailInviteTwo
            )}`}
            onConfirm={() => void sendEmailToParticipant()}
            onCancel={() => toggleInviteParticipantModal(false)}
            data-testid="invite-single-participant-modal"
          />
          <ConfirmModal
            visible={addParticipantModal}
            title={t(i18nKeys.participants.messages.sendInviteCallback)}
            message={`${t(i18nKeys.participants.messages.sendEmailInviteOne)} ${tempUserEmail?.length} ${t(
              i18nKeys.participants.messages.sendEmailInviteTwo
            )} ${t(i18nKeys.participants.messages.sendEmailInviteThree)}`}
            cancelBtnLabel={t(i18nKeys.participants.buttons.goBackBtn)}
            confirmBtnLabel={`${t(i18nKeys.participants.buttons.saveEmailInviteOne)} ${tempUserEmail?.length} ${t(
              i18nKeys.participants.buttons.saveEmailInviteTwo
            )}`}
            onConfirm={() => void sendEmailToParticipant()}
            onCancel={() => setAddParticipantModal(false)}
            data-testid="add-participant-modal"
          />
        </>
      )}
    </React.Fragment>
  );
};
